import { EnvironmentType } from 'constants/environment';
import { Scope } from 'constants/scope';
import { getConfig } from 'helpers/config';
import { getEnvironment } from 'helpers/feature-toggle';
import type { ProductData } from 'services/api/accounts/interfaces';
import { trackEvent } from 'services/event-tracking';

import { MIGRATION_WINDOW_DURATION, type LegacyMigrationEvent } from './constants';

const BILLING_SCOPES = [Scope.SubscriptionRW, Scope.SubscriptionLCRW, Scope.SubscriptionCBRW, Scope.SubscriptionHDRW];

export function hasLegacyMigrationBillingPermission(scopes: Scope[]): boolean {
  return scopes.some((scope) => BILLING_SCOPES.includes(scope));
}

export function getTextAppProduct(products: ReadonlyArray<ProductData>): ProductData | undefined {
  return products.find(({ product }) => product === 'TextApp');
}

export function hasLegacyMigrationProducts(products: ReadonlyArray<Pick<ProductData, 'product'>>): boolean {
  const installedProducts = new Set<ProductData['product']>(products.map(({ product }) => product));

  return installedProducts.has('LiveChat') && installedProducts.has('TextApp');
}

export function isLegacyMigrationSurfaceDismissed(
  permanentlyDismissed: boolean,
  dismissedUntil: number,
  now: number = Date.now(),
): boolean {
  return permanentlyDismissed || dismissedUntil > now;
}

export function getLegacyMigrationLandingUrl(): string {
  return `${getConfig().textAppUrl}/settings/billing/transition`;
}

export function getAiAgentActivationUrl(): string {
  return `${getConfig().textAppUrl}/team/ai-agents/`;
}

export function getDaysUntilMigration(textAppCreatedAt: string, now: number = Date.now()): number {
  const daysSinceCreated = (now - new Date(textAppCreatedAt).getTime()) / (24 * 60 * 60 * 1000);

  return Math.max(0, Math.ceil(MIGRATION_WINDOW_DURATION - daysSinceCreated));
}

export function getLegacyMigrationModalAssets(): { light: string; dark: string } {
  const env = getEnvironment();
  const cdn = env === EnvironmentType.Production ? 'cdn' : `cdn.${env}`;
  const base = `https://${cdn}.static-text.com/assets/livechat/migration-modal`;

  return {
    light: `${base}/migration_modal_light.webp`,
    dark: `${base}/migration_modal_dark.webp`,
  };
}

export function trackLegacyMigrationEvent(
  event: LegacyMigrationEvent,
  extraProperties: Record<string, string> = {},
): void {
  trackEvent(event, null, {
    // The historical Ampli event schema uses snake_case for this property.
    // eslint-disable-next-line @typescript-eslint/naming-convention
    migration_segment: 'forced',
    ...extraProperties,
  });
}
